import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import {
	InspectionTemplate,
	InspectionTemplateColumn,
	InspectionTemplateDraft,
} from "@/shared/models/Inspection";
import { upsertInspectionTemplate } from "@/shared/repositories/InspectionTemplateRepository";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { MesfModal } from "@dexteel/mesf-core";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, Grid2, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useInspectionTemplateCatalog } from "../hooks/useInspectionTemplateCatalog";
import {
	asNewColumns,
	columnsOfTemplate,
	sectionRevisions,
} from "../utils/templateDrafts";
import { TemplateModalTarget } from "../utils/templateModal";
import { CloneSectionDialog } from "./CloneSectionDialog";
import { TemplateColumnsEditor } from "./TemplateColumnsEditor";
import { TemplateDrawingField } from "./TemplateDrawingField";

type Props = {
	target: TemplateModalTarget;
	onHide: () => void;
	onSaved: () => void;
	onError: (message: string) => void;
	onDirtyChange: (isDirty: boolean) => void;
};

export const TemplateDraftForm = ({
	target,
	onHide,
	onSaved,
	onError,
	onDirtyChange,
}: Props) => {
	const { mode, draft, sectionName } = target;

	const queryClient = useQueryClient();
	const { data } = useInspectionTemplateCatalog();

	const [form, setForm] = useState<InspectionTemplateDraft>(draft);
	const [drawingName, setDrawingName] = useState<string | null>(null);
	const [isCloning, setIsCloning] = useState(false);

	const templates = data?.templates ?? [];
	const columns = data?.columns ?? [];
	const sections = data?.sections ?? [];

	const isView = mode === "view";
	const isDirty = !isView && JSON.stringify(form) !== JSON.stringify(draft);
	const isSectionFixed = mode !== "create" && mode !== "duplicate";

	const revisions = sectionRevisions(templates, form.SectionId);

	const isCorrection = mode === "correct";
	const pendingRevision =
		mode === "update" || isCorrection ? null : revisions.pending;

	const isBlankSection =
		(mode === "create" || mode === "revision") &&
		form.SectionId !== null &&
		revisions.latest === null;

	const targetSectionName =
		sections.find((section) => section.SectionId === form.SectionId)
			?.SectionName ||
		sectionName ||
		"this section";

	const pickSection = (sectionId: number | null) => {
		if (sectionId === null) {
			setForm((current) => ({ ...current, SectionId: null }));
			return;
		}

		const latest = sectionRevisions(templates, sectionId).latest;
		const carriesSheet = mode === "create" && latest !== null;

		setForm((current) => ({
			...current,
			SectionId: sectionId,
			UploadFileId: carriesSheet ? latest.UploadFileId : current.UploadFileId,
			Columns: carriesSheet
				? asNewColumns(columnsOfTemplate(columns, latest))
				: current.Columns,
		}));

		if (carriesSheet) setDrawingName(latest.DrawingFileName);
	};

	const cloneFrom = (template: InspectionTemplate, withDrawing: boolean) => {
		setForm((current) => ({
			...current,
			UploadFileId: withDrawing ? template.UploadFileId : null,
			Columns: asNewColumns(columnsOfTemplate(columns, template)),
		}));
		setDrawingName(withDrawing ? template.DrawingFileName : null);
		setIsCloning(false);
	};

	const upsertMutation = useMutation(upsertInspectionTemplate, {
		meta: {
			successMessage: isCorrection
				? "Published revision updated"
				: "Draft saved",
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["inspection-templates"],
			});
			onSaved();
		},
		onError: (mutationError: Error) => onError(mutationError.message),
	});

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		if (isView) return;

		if (form.Columns.some((column) => column.ColumnLabel.trim() === "")) {
			onError("Every column needs a label.");
			return;
		}

		upsertMutation.mutate(form);
	};

	useEffect(() => {
		onDirtyChange(isDirty);
	}, [isDirty]);

	return (
		<>
			<form
				data-test-id="template-draft-form"
				onSubmit={onSubmit}
				style={{
					display: "flex",
					flexDirection: "column",
					flex: "1 1 auto",
					minHeight: 0,
					overflow: "hidden",
				}}
			>
				<MesfModal.Content
					style={{ padding: "15px 30px", overflowY: "auto", minHeight: 0 }}
					dividers
				>
					<Grid2 container spacing={2}>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<SectionSelector
								value={form.SectionId}
								size="small"
								required
								assetId={MILL_1.id}
								disabled={isSectionFixed}
								onChange={pickSection}
							/>
						</Grid2>
						<Grid2 size={{ md: 9, xs: 12 }}>
							<TemplateDrawingField
								uploadFileId={form.UploadFileId}
								fileName={drawingName}
								templates={templates}
								sectionName={targetSectionName}
								disabled={isView}
								onChange={(uploadFileId, fileName) => {
									setForm((current) => ({
										...current,
										UploadFileId: uploadFileId,
									}));
									setDrawingName(fileName);
								}}
								onError={onError}
							/>
						</Grid2>

						{!isView && pendingRevision !== null && (
							<Grid2 size={{ xs: 12 }}>
								<Typography
									variant="caption"
									color="error"
									data-test-id="pending-revision-warning"
								>
									{`${targetSectionName} already has a ${
										pendingRevision.Rev === null
											? "draft revision. Edit it or delete it"
											: `revision scheduled (Rev ${pendingRevision.Rev}). Change it to Draft or delete it`
									} before starting a new draft.`}
								</Typography>
							</Grid2>
						)}

						{isCorrection && (
							<Grid2 size={{ xs: 12 }}>
								<Typography
									variant="caption"
									color="warning"
									data-test-id="editing-published-warning"
								>
									This revision is in force. The changes apply to the sheets
									already inspected under it; columns that were already read
									cannot be removed or change their type.
								</Typography>
							</Grid2>
						)}

						{!isView && !isCorrection && form.SectionId !== null && (
							<Grid2
								size={{ xs: 12 }}
								container
								alignItems="center"
								spacing={1}
							>
								<Button
									variant="outlined"
									size="small"
									startIcon={<ContentCopyIcon />}
									data-test-id="clone-section-button"
									onClick={() => setIsCloning(true)}
								>
									Clone from another section
								</Button>
								{isBlankSection && (
									<Typography
										variant="caption"
										color="text.secondary"
										data-test-id="blank-section-hint"
									>
										{`${targetSectionName} has no sheet yet: write its columns below, or start from the sheet of a section that is already inspected.`}
									</Typography>
								)}
							</Grid2>
						)}

						<Grid2 size={{ xs: 12 }}>
							<TemplateColumnsEditor
								columns={form.Columns}
								disabled={isView}
								onChange={(nextColumns: InspectionTemplateColumn[]) =>
									setForm((current) => ({ ...current, Columns: nextColumns }))
								}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					{isView ? (
						<Grid2 container justifyContent="flex-end">
							<Button variant="contained" color="inherit" onClick={onHide}>
								Close
							</Button>
						</Grid2>
					) : (
						<FooterModalButtons
							isSubmitLoading={upsertMutation.isLoading}
							onHide={onHide}
							buttonLabel="Save"
							buttonColor="primary"
							disabled={form.SectionId === null || pendingRevision !== null}
						/>
					)}
				</MesfModal.Actions>
			</form>
			{isCloning && (
				<CloneSectionDialog
					templates={templates}
					sectionId={form.SectionId}
					sectionName={targetSectionName}
					onClose={() => setIsCloning(false)}
					onClone={cloneFrom}
				/>
			)}
		</>
	);
};
